import { useEffect } from "react";
import { Input, Tooltip } from "antd";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { IconHelp } from "@icons/index";
import { MultiImageUpload } from "@pages/video/components/ImageUpload/MultiImageUpload";
import { Section } from "@src/ui/Section";
import type { BasicInfoData, ProductImage } from "@pages/videoCreating/videoCreatingSlice";
import { MAX_IMAGE_COUNT } from "@utils/const";
import { useSerializeCallback } from "@kalodata/tools";
import { useLatest } from "ahooks";
import { useGetProductInfo } from "@hooks/useGetProductInfo";

export const shotDemoList = [
  { title: "Front", url: "ff8e4944.jpg" },
  { title: "Side", url: "ce31fbde.jpg" },
  { title: "Back", url: "43d19ece.jpg" },
  { title: "Top", url: "6d7f3fd0.jpg" },
  { title: "Bottom", url: "eb2361fe.jpg" },
  { title: "Lifestyle", url: "c647fdff.png" },
  // { title: "Left", url: "2202f0ce.jpg" },
  // { title: "Front Left", url: "a76b2c66.jpg" },
  // { title: "Back Left", url: "4139ec35.jpg" },
];

interface BasicInfoContentProps {
  productId?: string;
  country?: string;
  images: ProductImage[];
  productTitle: string;
  productCategory: string;
  onImagesChange: (images: ProductImage[]) => void;
  onUploadingChange?: (uploading: boolean) => void;
  onChangeBasicInfo: (basicInfo: Partial<BasicInfoData>) => void;
  /** 是否正在AI分析 */
  isAIAnalyzing?: boolean;
  onParseProductFailed?: () => void;
}

export const BasicInfoContent = ({
  productId,
  country,
  images,
  productTitle,
  onImagesChange,
  onUploadingChange,
  productCategory,
  isAIAnalyzing,
  onChangeBasicInfo,
  onParseProductFailed,
}: BasicInfoContentProps) => {
  const getProductInfo = useGetProductInfo();
  const onParseProductFailedFn = useLatest(onParseProductFailed);
  const getProductInformation = useSerializeCallback(async () => {
    if (!productId || productId === "custom") return;
    const parsed = await getProductInfo({ id: productId, country });
    if (!parsed) {
      onParseProductFailedFn.current?.();
      return;
    }
    const { images: parsedImages, product, linkSource } = parsed;
    const productTitleStr = product?.productTitle || "";
    const { priCateName, secCateName, terCateName } = product || {};
    const categoryParts = [priCateName, secCateName, terCateName].filter(Boolean);

    onChangeBasicInfo({
      productTitle: productTitleStr,
      linkSource,
      productCategory: categoryParts.length > 0 ? categoryParts.join(" > ") : "",
    });

    if (parsedImages && parsedImages.length > 0) {
      const newImages: ProductImage[] = parsedImages
        .filter((item) => item.success && item.imageUrl)
        .slice(0, MAX_IMAGE_COUNT)
        .map(
          (item) =>
            ({
              id: `product-image-${item.assetId || 0}`,
              assetId: item.assetId,
              url: item.imageUrl || "",
              description: item.description ?? "",
              isChangedUrl: false,
              aiAnalyzed: true,
              needsFaceMasking: false,
            }) satisfies ProductImage,
        );
      onImagesChange(newImages);
    }
  });

  useEffect(() => {
    if (productId) {
      getProductInformation();
    }
  }, [productId, country, getProductInformation]);

  return (
    <>
      {/* Product Image */}
      <section className="mb-[16px]">
        <div className="flex items-center justify-between mb-[4px]">
          <div className="flex items-center gap-1">
            <span className="text-kds-fg font-bold text-[16px]">
              <Trans>Product Image</Trans>
            </span>
            <span className="text-kds-danger">*</span>
            <Tooltip title={t("Upload at least 1 image for the best video results.")}>
              <span>
                <IconHelp className="w-[16px] h-[16px] text-kds-fg-subtle cursor-pointer" />
              </span>
            </Tooltip>
          </div>
          {/* <Popover
            content={
              <div className="grid grid-cols-4 gap-y-[8px] gap-x-[4px] p-[8px] pb-[16px]">
                {shotDemoList.map((item) => (
                  <div key={item.title} className="w-[120px] flex flex-col items-center">
                    <UploadImage name={item.url} className="w-[120px] h-[120px]" />
                    <span className="font-semibold text-kds-fg mt-[6px] text-[13px] whitespace-nowrap">
                      <Trans>{item.title}</Trans>
                    </span>
                  </div>
                ))}
              </div>
            }
            trigger="click"
            placement="leftTop"
          >
            <span className="kalo-text-linear-blue text-[14px] font-normal cursor-pointer">
              <Trans>Guide</Trans>
            </span>
          </Popover> */}
        </div>
        <p className="text-kds-fg-subtle text-[14px] mb-[8px] leading-[1.2]">
          <Trans>
            Upload multi-angle photos of a single SKU (style/color). Avoid mixing different variants, and exclude images
            with excessive text, accessories (e.g., replacement brush heads), or group shots (e.g., product sets).
          </Trans>
        </p>
        {getProductInformation.isPending() || isAIAnalyzing ? (
          <Section
            className="bg-kds-bg-card border border-kds-border-subtle rounded-[8px] flex flex-col min-h-[160px] overflow-hidden"
            status={"loading"}
            error={undefined}
            loadingText="Checking image compliance..."
          />
        ) : (
          <MultiImageUpload
            maxCount={MAX_IMAGE_COUNT}
            dataList={images}
            onChange={onImagesChange}
            onUploadingChange={onUploadingChange}
          />
        )}
      </section>
      {/* Product Name */}
      <section className="mb-[16px]">
        <div className="flex items-center gap-1 mb-[4px]">
          <span className="text-kds-fg font-bold text-[16px]">
            <Trans>Product Name</Trans>
          </span>
          <span className="text-kds-danger">*</span>
          {/* <Tooltip title={t("Enter your product name for the video")}>
            <span>
              <IconHelp className="w-[16px] h-[16px] text-kds-fg-subtle cursor-pointer" />
            </span>
          </Tooltip> */}
        </div>
        <Input
          value={productTitle}
          onChange={(e) => {
            onChangeBasicInfo?.({ productTitle: e.target.value });
          }}
          placeholder={t("e.g., Summer Dress Collection")}
          className="h-[44px] bg-kds-bg-card border-none text-kds-fg placeholder:text-kds-fg-subtle"
        />
      </section>

      {/* Product Category */}
      <section className="mb-[16px]">
        <div className="flex items-center gap-1 mb-[8px]">
          <span className="text-kds-fg font-bold text-[16px]">
            <Trans>Category</Trans>
          </span>
          <span className="text-kds-danger">*</span>
          <Tooltip title={t("Enter the category that best describes your product")}>
            <span>
              <IconHelp className="w-[16px] h-[16px] text-kds-fg-subtle cursor-pointer" />
            </span>
          </Tooltip>
        </div>
        <Input
          value={productCategory}
          onChange={(e) => onChangeBasicInfo?.({ productCategory: e.target.value })}
          placeholder={t("e.g., Beauty & Personal Care")}
          className="h-[44px] bg-kds-bg-card border-none text-kds-fg-soft placeholder:text-kds-fg-subtle"
        />
      </section>
    </>
  );
};
